import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Transaction } from '../../../../../../Overview/types';
import {
  Band,
  IndicatorConfig,
  IndicatorDataPoint,
  IndicatorSingleValue,
  RSIItem,
  ZoningBand,
} from '../../../../../../ProfitCalculations/types';
import './index.css';
import { colors } from '../../../../../../../colors';
import {
  DomainTuple,
  Point,
  VictoryAxis,
  VictoryCandlestick,
  VictoryChart,
  VictoryLabel,
  VictoryLine,
  VictoryScatter,
  VictoryTooltip,
} from 'victory';
import moment from 'moment';
import {
  formatBalance,
  formatDate,
  formatNumber,
  formatPercentage,
  formatPrice,
} from '../../../../../../../helpers/NumberFormatter';
import {
  CbrtRangeState,
  MrcState,
  WakeupTimes,
} from '../../../../../../ProfitCalculations/types';
import { BiExitFullscreen } from 'react-icons/bi';
import {
  createXTickValues,
  generateTooltipLabelGenericTransaction,
  getFormatUnit,
} from '../../../../../../../helpers/bundleTransactionsToCompactForm';
import { darkenColor } from '../../../../../../../helpers/colors';
import MultiChart, {
  XAxisInfo,
} from '../../../../../../../components/MultiChart';
import { ComponentDimensions } from '../../../../../../../types/Layout';
import BandChart from '../../../../../../../components/Charts/CandleSticks/BandChart';
import { isZoningBand } from '../../../../../../../components/Charts/CandleSticks';
import {
  DataPoint,
  createYAxisCandlesticksTicks,
  formatToCandlestickDataPointType,
  formatYTick,
} from '../../../../../../../helpers/Chart';
import { BotTimeFrames, Candle, Scale } from '../../../../../../Detail/types';
import { getTimeFrameIntervalTimestamp } from '../../../../../../Detail/Charts/PriceChart/helpers';
import { CompactTransaction } from '../../../../../../../types/subscription';
import { GenericTransactionInfo } from '../../../../../../../types/Transaction';
import { TimeFrame } from '../../../../../../../types/Bot';
import RSIChart from '../../../../../../../components/Charts/Rsi';

type MovingAverage = {
  x: number;
  y: number;
};

export type CandleInfo = {
  high: number;
  close: number;
  open: number;
  low: number;
  startTimestamp: number;
  endTimestamp: number;
  direction: string;
};

export type MrcStateDataPoint = {
  x: number;
  y: number;
  color: string;
  date?: Date;
  label?: string;
  orderType?: string;
};

interface Props {
  isCompact: boolean;
  transaction: GenericTransactionInfo;
  compactTransactions: GenericTransactionInfo[];
  handleCloseChartView: () => void;
  cbrtRangeStates: CbrtRangeState[] | undefined;
  mrcStates: MrcState[] | undefined;
  timeFrames?: BotTimeFrames | undefined;
  wakeupTimes?: WakeupTimes;
  clusteredTransactions: GenericTransactionInfo[];
  indicatorConfig?: IndicatorConfig;
  rsiValues?: IndicatorDataPoint[] | undefined;
  rsiIndicatorValuesOppositeOrder?: RSIItem[] | undefined;
  rsiIndicatorValuesSameOrder?: RSIItem[] | undefined;
}

interface ChartProps {
  timeFrame: TimeFrame | undefined;
  transaction: GenericTransactionInfo;
  higherTCandles: Candle[] | undefined;
  cbrtRangeStates: CbrtRangeState[] | undefined;
  mrcStates: MrcState[] | undefined;
  density: number;
  isCompare: boolean;
  profitCalcsWakeUpTimes: WakeupTimes | undefined;
  isCompact: boolean;
  compactTransactions: GenericTransactionInfo[];
  clusteredTransactions: GenericTransactionInfo[];
  transactionCandles: Candle[] | undefined;
  setCandleInfo: React.Dispatch<React.SetStateAction<CandleInfo | undefined>>;

  height?: number | undefined;
  width?: number | undefined;
  setXAxisInfo?: React.Dispatch<React.SetStateAction<XAxisInfo | undefined>>;
  movingAverages?: Array<{
    data: IndicatorSingleValue[];
    color: string;
    label: string;
  }>;
  bollingerBand?: {
    data: Band | ZoningBand | undefined;
    signalPeriod: number | null;
  };
  keltnerChannel?: {
    data: Band | ZoningBand | undefined;
    signalPeriod: number | null;
  };
}

const Chart = (props: ChartProps) => {
  const {
    transaction,
    cbrtRangeStates,
    mrcStates,
    density,
    timeFrame,
    isCompare,
    isCompact,
    higherTCandles,
    profitCalcsWakeUpTimes,
    compactTransactions,
    clusteredTransactions,
    transactionCandles,
    setCandleInfo,
  } = props;

  const {
    height,
    width,
    setXAxisInfo,
    movingAverages,
    bollingerBand,
    keltnerChannel,
  } = props;

  const candleDataPoints: DataPoint[] = useMemo(() => {
    const data = transactionCandles;

    if (!data?.length) {
      return [];
    }

    return data.map((d) => formatToCandlestickDataPointType(d));
  }, [transactionCandles]);

  const tickValues = useMemo(() => {
    if (!candleDataPoints.length) {
      return [];
    }

    return createXTickValues(
      candleDataPoints[0].x,
      candleDataPoints[candleDataPoints.length - 1].x,
    );
  }, [candleDataPoints]);

  const findClosestCandleTimestamp = useCallback(
    (timestamp: number) => {
      if (!candleDataPoints) {
        return timestamp;
      }

      return (
        candleDataPoints.find((c) => c.x <= timestamp && timestamp <= c.x2)
          ?.x || 0
      );
    },
    [candleDataPoints],
  );

  const getTransactionTotalValue = useCallback(
    (transaction: any /*Transaction | CustomTransaction*/) => {
      return (
        transaction.currentStableBalance +
        transaction.currentAssetBalance *
          (transaction.orderPrice ?? transaction.currentPrice)
      );
    },
    [],
  );

  const getTransactionTotalAssetValue = useCallback(
    (transaction: any /*Transaction | CustomTransaction*/) => {
      return (
        transaction.currentStableBalance /
          (transaction.orderPrice ?? transaction.currentPrice) +
        transaction.currentAssetBalance
      );
    },
    [],
  );

  const generateTooltipLabel = useCallback(
    (
      transaction: Transaction | CompactTransaction,
      profitValue: number | null,
    ) => {
      const formattedDate = formatDate(transaction.transactionTimestamp || 0);

      const isCustomTransaction = transaction.hasOwnProperty('isDistribution');

      if (isCustomTransaction) {
        const transactionType = transaction.transactionType;
        const isStableBalance = transactionType.indexOf('STABLE') !== -1;
        const stableAmount = formatBalance(transaction.stableQuantity, true);
        const assetAmount = `${formatBalance(
          transaction.assetQuantity,
          false,
        )}`;

        const amount = isStableBalance
          ? `${stableAmount} (${assetAmount})`
          : `${assetAmount} (${stableAmount})`;

        return `${formattedDate}\n${transactionType}\n${amount}`;
      }

      const isBuy = transaction.orderType === 'BUY';

      const stableBalance = formatBalance(
        getTransactionTotalValue(transaction),
      );
      const assetBalance = `${formatBalance(
        getTransactionTotalAssetValue(transaction),
        false,
      )}`;
      const balance = isBuy ? assetBalance : stableBalance;

      const profit = formatPercentage(profitValue || 0);
      const price = formatPrice(
        transaction.averageOrderPrice || transaction.orderPrice,
        true,
        6,
      );

      return `${formattedDate}\n${balance} | ${profit}\n${price}`;
    },
    [getTransactionTotalAssetValue, getTransactionTotalValue],
  );

  const transactionDataPoints = useMemo(() => {
    if (!isCompact) {
      return [
        {
          x: findClosestCandleTimestamp(transaction.transactionTimestamp || 0),
          y: transaction.orderPrice,
          color: transaction.orderType === 'BUY' ? colors.green : colors.red,
          label: generateTooltipLabelGenericTransaction(transaction),
          strokeColor: transaction.transactionType.includes('STOP_LOSS')
            ? colors.blue
            : transaction.transactionType.includes('TAKE_PROFIT')
            ? colors.yellow
            : colors.white,
          size: transaction.transactionType.includes('STOP_LOSS')
            ? 5
            : transaction.transactionType.includes('TAKE_PROFIT')
            ? 5
            : 8,
        },
      ];
    }

    const index = compactTransactions.findIndex(
      (t) =>
        t.compactTransactionId?.toString() === transaction.compactTransactionId,
    );
    const updatedCompactTransactions = [...compactTransactions];

    if (density === 3) {
      updatedCompactTransactions.splice(index, 1, ...clusteredTransactions);
    }

    const dataPoints = [];

    // if (isCompare && profitCalcsTransactions.length) {
    //   if (density !== 3 && bot) {
    //     const profitCompactTransactions = bundleTransactionsToCompactForm(
    //       profitCalcsTransactions,
    //     );

    //     dataPoints.push(
    //       ...formatTransactionType(profitCompactTransactions, bot).map((t) => {
    //         return {
    //           x: findClosestCandleTimestamp(t.transactionTimestamp || 0),
    //           y: t.averageOrderPrice || t.orderPrice,
    //           color: colors.blue,
    //           label: generateTooltipLabel(
    //             t,
    //             (t as unknown as Transaction).profit,
    //           ),
    //           strokeColor: t.transactionType.includes('TAKE_PROFIT')
    //             ? colors.blue
    //             : colors.white,
    //         };
    //       }),
    //     );
    //   } else if (bot) {
    //     dataPoints.push(
    //       ...formatTransactionType(profitCalcsTransactions, bot).map((t) => {
    //         return {
    //           x: findClosestCandleTimestamp(t.transactionTimestamp),
    //           y: t.orderPrice,
    //           color: colors.blue,
    //           label: generateTooltipLabel(
    //             t,
    //             (t as unknown as Transaction).profit,
    //           ),
    //           strokeColor: t.transactionType.includes('TAKE_PROFIT')
    //             ? colors.blue
    //             : colors.white,
    //         };
    //       }),
    //     );
    //   }
    // }

    dataPoints.push(
      ...updatedCompactTransactions.map((t) => {
        return {
          x: findClosestCandleTimestamp(t.transactionTimestamp || 0),
          y: t.orderPrice,
          color: (t.orderType === 'BUY' ? colors.green : colors.red) as string,
          label: generateTooltipLabelGenericTransaction(t),
          strokeColor: t.transactionType.includes('STOP_LOSS')
            ? colors.blue
            : t.transactionType.includes('TAKE_PROFIT')
            ? colors.yellow
            : colors.white,
          size: t.transactionType.includes('STOP_LOSS')
            ? 3
            : t.transactionType.includes('TAKE_PROFIT')
            ? 3
            : 5,
        };
      }),
    );

    return dataPoints;
  }, [
    clusteredTransactions,
    compactTransactions,
    density,
    findClosestCandleTimestamp,
    isCompact,
    transaction,
  ]);

  const getCandleColor = useCallback(
    (c: any) => {
      if (density === 1) {
        return c.datum.close >= c.datum.open ? '#008000' : '#FF0000';
      }

      if (density === 3 && !isCompare) {
        return c.datum.close >= c.datum.open ? '#008000' : '#FF0000';
      }

      const data: any[] = [];

      if (profitCalcsWakeUpTimes) {
        if (density === 2) {
          !!data
            ? data.push(...profitCalcsWakeUpTimes.shorterT)
            : [...profitCalcsWakeUpTimes.shorterT];
        } else if (density === 3) {
          !!data
            ? data.push(...profitCalcsWakeUpTimes.multiT)
            : [...profitCalcsWakeUpTimes.multiT];
        }
      }

      if (!data) {
        return c.datum.close >= c.datum.open ? '#008000' : '#FF0000';
      }

      // We basically want to do a CROSS JOIN between the real and profitCalcs wakeup times
      // We want to find all the wakeup times that only exists in 1 of the two arrays
      // We want this format: { wakeupTime: amount }
      const amountByWakeupTime = data.reduce<Record<string, number>>(
        (acc, curr) => {
          if (!acc[curr]) {
            acc[curr] = 1;
            return acc;
          }

          acc[curr] = acc[curr] + 1;
          return acc;
        },
        {},
      );

      if (density !== 1) {
        if (isCompare) {
          const amount = amountByWakeupTime[c.datum.startTimestamp];

          if (amount === 1) {
            return colors.white;
          }

          return c.datum.close >= c.datum.open ? '#008000' : '#FF0000';
        }

        if (data.includes(c.datum.startTimestamp)) {
          return c.datum.close >= c.datum.open ? '#008000' : '#FF0000';
        }
      }

      return darkenColor(
        c.datum.close >= c.datum.open ? '#008000' : '#FF0000',
        30,
      );
    },

    [density, isCompare, profitCalcsWakeUpTimes],
  );

  const calculateTickValue = useCallback(
    (t: Date) => {
      if (!candleDataPoints?.length) {
        return moment(t).format('DD MMM');
      }

      const formatUnit = getFormatUnit(
        candleDataPoints[0].x,
        candleDataPoints[candleDataPoints.length - 1].x,
      );

      return moment(t).format(formatUnit);
    },
    [candleDataPoints],
  );

  const axisStyle = useMemo(
    () => ({
      axis: { stroke: 'rgba(64,64,64,0.7)' },
      grid: {
        stroke: 'rgba(64,64,64,0.7)',
      },
      ticks: setXAxisInfo ? {} : { stroke: 'white', size: 2 },
      tickLabels: { fontSize: 14, padding: 5, fill: 'gray' },
    }),
    [setXAxisInfo],
  );

  const yAxisStyle = useMemo(
    () => ({
      axis: { stroke: 'rgba(64,64,64,0.7)' },
      grid: {
        stroke: 'rgba(64,64,64,0.7)',
      },
      ticks: { stroke: 'white', size: 2 },
      tickLabels: { fontSize: 20, padding: 5, fill: '#999' },
    }),
    [],
  );

  const cbrtLines = useMemo(() => {
    if (!cbrtRangeStates?.length || !candleDataPoints?.length) {
      return [];
    }

    const filteredCbrtRangeStates: Array<
      {
        x: number;
        y: number;
        color: string;
        label?: string;
      }[]
    > = [];

    const visibleCbrtRangeStates = cbrtRangeStates.filter(
      (s) =>
        (candleDataPoints[0].x <= s.timestamp ||
          s.timestamp <= transaction.transactionTimestamp) &&
        (s.endTimestamp || new Date().valueOf()) > candleDataPoints[0].x,
    );

    visibleCbrtRangeStates.forEach((cbrtRangeState, index) => {
      const isLast = index === visibleCbrtRangeStates.length - 1;

      const color =
        cbrtRangeState.direction === 'up' ? colors.green : colors.red;
      const close =
        cbrtRangeState.open +
        cbrtRangeState.cbrt *
          cbrtRangeState.open *
          (cbrtRangeState.direction === 'up' ? 1 : -1);

      const negativeClose = cbrtRangeState.open - (close - cbrtRangeState.open);

      /** Break positive side */
      filteredCbrtRangeStates.push([
        {
          x: cbrtRangeState.timestamp,
          y: close,
          color: isLast ? color : darkenColor(color, 20),
          label: `cbrt: ${formatNumber(close, 4)}`,
        },
        {
          x: cbrtRangeState.endTimestamp || new Date().valueOf(),
          y: close,
          color: isLast ? color : darkenColor(color, 20),
        },
      ]);

      const negativeColor =
        cbrtRangeState.direction === 'up'
          ? `${colors.red}66`
          : `${colors.green}66`;

      /** Break negative side */
      filteredCbrtRangeStates.push([
        {
          x: cbrtRangeState.timestamp,
          y: negativeClose,
          color: negativeColor,
          label: `cbrt: ${formatNumber(negativeClose, 4)}`,
        },
        {
          x: cbrtRangeState.endTimestamp || new Date().valueOf(),
          y: negativeClose,
          color: negativeColor,
        },
      ]);
    });

    return filteredCbrtRangeStates;
  }, [candleDataPoints, cbrtRangeStates, transaction.transactionTimestamp]);

  const mrcStatesLines = useMemo(() => {
    if (!mrcStates?.length || !candleDataPoints?.length || !timeFrame) {
      return [];
    }

    /** For each item, make a line with a start and end */
    const items: Array<Array<MrcStateDataPoint>> = [];

    const visibleMrcStates = mrcStates.filter(
      (s) =>
        candleDataPoints[0].x <= (s.endTimestamp || new Date().valueOf()) &&
        (s.endTimestamp || new Date().valueOf()) <=
          candleDataPoints[candleDataPoints.length - 1].x2 &&
        transaction.orderType === s.orderType,
    );

    visibleMrcStates.forEach((mrcState) => {
      if (mrcState.startTimestamp === mrcState.endTimestamp) {
        return;
      }

      if (mrcState.orderType !== transaction.orderType) {
        return;
      }

      const baseColor =
        mrcState.orderType === 'BUY' ? colors.green : colors.red;
      const color =
        mrcState.state === 'INITIALISED'
          ? darkenColor(baseColor, 10)
          : mrcState.state === 'PENDING'
          ? darkenColor(baseColor, 50)
          : baseColor;

      const start = mrcState.startTimestamp;
      const end = mrcState.endTimestamp || new Date().valueOf();

      if (
        start <= candleDataPoints[0].x ||
        end > candleDataPoints[candleDataPoints.length - 1].x
      ) {
        return;
      }

      /** If the state is longer than 1 candle */
      const isLongEnough = !timeFrame
        ? true
        : end - start >= getTimeFrameIntervalTimestamp(timeFrame);

      if (!isLongEnough) {
        return;
      }

      items.push([
        {
          x: start,
          y: mrcState.startPrice,
          color,
          date: new Date(start),
          label: `mrc: ${mrcState.state}`,
          orderType: mrcState.orderType,
        },
        {
          x: end,
          y: mrcState.startPrice,
          color,
          date: new Date(end),
        },
      ]);
    });

    return items;
  }, [candleDataPoints, mrcStates, timeFrame, transaction.orderType]);

  const channelMin = useMemo(() => {
    const channel = bollingerBand?.data || keltnerChannel?.data;

    if (!channel) {
      return Infinity;
    }

    if (isZoningBand(channel)) {
      return Math.min(
        ...channel.buyBand
          .filter(
            (c) =>
              candleDataPoints[0].x <= c.timestamp &&
              c.timestamp <= candleDataPoints[candleDataPoints.length - 1].x,
          )
          .map((c) => c.value),
      );
    }

    return Math.min(
      ...channel.lowerBand
        .filter(
          (c) =>
            candleDataPoints[0].x <= c.timestamp &&
            c.timestamp <= candleDataPoints[candleDataPoints.length - 1].x,
        )
        .map((c) => c.value),
    );
  }, [bollingerBand?.data, candleDataPoints, keltnerChannel?.data]);

  const channelMax = useMemo(() => {
    const channel = bollingerBand?.data || keltnerChannel?.data;

    if (!channel) {
      return -Infinity;
    }

    if (isZoningBand(channel)) {
      return Math.max(
        ...channel.sellBand
          .filter(
            (c) =>
              candleDataPoints[0].x <= c.timestamp &&
              c.timestamp <= candleDataPoints[candleDataPoints.length - 1].x,
          )
          .map((c) => c.value),
      );
    }

    return Math.max(
      ...channel.upperBand
        .filter(
          (c) =>
            candleDataPoints[0].x <= c.timestamp &&
            c.timestamp <= candleDataPoints[candleDataPoints.length - 1].x,
        )
        .map((c) => c.value),
    );
  }, [bollingerBand?.data, candleDataPoints, keltnerChannel?.data]);

  const domain = useMemo(() => {
    if (!candleDataPoints?.length) {
      return undefined;
    }

    const minY = Math.min(
      ...candleDataPoints.map((c) => c.low),
      ...cbrtLines.map((cl) => Math.min(...cl.map((c) => c.y))),
      ...mrcStatesLines.map((ms) => Math.min(...ms.map((s) => s.y))),
      channelMin,
    );

    const maxY = Math.max(
      ...candleDataPoints.map((c) => c.high),
      ...cbrtLines.map((cl) => Math.min(...cl.map((c) => c.y))),
      ...mrcStatesLines.map((ms) => Math.max(...ms.map((s) => s.y))),
      channelMax,
    );

    return {
      x: [
        candleDataPoints[0].x,
        candleDataPoints[candleDataPoints.length - 1].x,
      ] as DomainTuple,
      y: [minY * 0.92, maxY * 1.05] as DomainTuple,
    };
  }, [candleDataPoints, cbrtLines, channelMax, channelMin, mrcStatesLines]);

  const movingAveragesDataSets = useMemo(() => {
    if (!movingAverages?.length) {
      return [];
    }
    const dataSets: Array<{ data: MovingAverage[]; color: string }> = [];

    movingAverages.forEach((movingAverage) => {
      dataSets.push({
        data: movingAverage.data.map((ma) => ({
          x: ma.timestamp,
          y: ma.value,
        })),
        color: movingAverage.color,
      });
    });

    return dataSets;
  }, [movingAverages]);

  const handleHover = useCallback(
    async (
      event: React.SyntheticEvent<any, Event>,
      eventProps: { index: number; data: any; datum: any },
    ) => {
      setCandleInfo({
        high: eventProps.datum.high,
        close: eventProps.datum.close,
        open: eventProps.datum.open,
        low: eventProps.datum.low,
        startTimestamp: eventProps.datum.x,
        endTimestamp: eventProps.datum.x2,
        direction: eventProps.datum.direction,
      });
    },
    [setCandleInfo],
  );

  const domainPadding = useMemo(() => {
    /** This state sometimes only shows a few candles
     * The fewer the candles, the closer they must be together, to higher the domainPadding
     * Use a minimum of 30
     */

    const amountOfCandles = candleDataPoints.length;
    return { x: Math.max(30, 68 - amountOfCandles) };
  }, [candleDataPoints.length]);

  const yTickValues = useMemo(() => {
    if (!domain) {
      return [];
    }

    return createYAxisCandlesticksTicks(
      candleDataPoints,
      compactTransactions,
      bollingerBand?.data || keltnerChannel?.data,
      domain,
    );
  }, [
    bollingerBand?.data,
    candleDataPoints,
    compactTransactions,
    domain,
    keltnerChannel?.data,
  ]);

  const paddingRight = useMemo(() => {
    if (!yTickValues?.length) {
      return 0;
    }

    const longestTick = yTickValues
      .sort((a, b) => b.toString().length - a.toString().length)[0]
      .toString();

    const length = longestTick.length;
    const hasDecimal = longestTick.includes(',') || longestTick.includes('.');
    return 45 + length * 10 - (hasDecimal ? 5 : 0);
  }, [yTickValues]);

  const chartPadding = useMemo(
    () => ({
      left: 0,
      right: paddingRight,
      bottom: setXAxisInfo ? 0 : 30,
      top: 50,
    }),
    [setXAxisInfo, paddingRight],
  );

  useEffect(() => {
    if (!domain?.x || !tickValues?.length) {
      return;
    }

    setXAxisInfo?.({
      tickValues,
      axisStyle,
      tickFormat: calculateTickValue,
      domain: { x: domain.x as [number, number] },
      domainPadding,
      chartPadding,
    });
  }, [
    axisStyle,
    calculateTickValue,
    chartPadding,
    domain?.x,
    domainPadding,
    setXAxisInfo,
    tickValues,
  ]);

  const chartWidth = width;
  const chartHeight = height ?? 0;

  return (
    <div
      className={'candlesticks-container'}
      style={{ width: chartWidth, height: chartHeight }}
    >
      <VictoryChart
        domainPadding={domainPadding}
        padding={chartPadding}
        domain={domain}
        width={chartWidth}
        height={chartHeight}
        scale={Scale.Logarithmic}
      >
        <VictoryAxis
          tickFormat={!setXAxisInfo ? calculateTickValue : () => ''}
          style={axisStyle}
          tickValues={tickValues}
          fixLabelOverlap
        />
        <VictoryAxis
          dependentAxis
          style={yAxisStyle}
          tickFormat={formatYTick}
          orientation={'right'}
          tickValues={yTickValues}
          tickLabelComponent={<VictoryLabel dx={5} />}
        />

        <BandChart band={bollingerBand?.data} domain={domain} />
        <BandChart band={keltnerChannel?.data} domain={domain} />

        {movingAveragesDataSets &&
          movingAveragesDataSets.map(({ data, color }, i) => (
            <VictoryLine
              key={`moving_average_${i}`}
              data={data}
              style={{
                data: {
                  stroke: color,
                  strokeWidth: 1,
                  opacity: 0.75,
                },
              }}
            />
          ))}

        <VictoryCandlestick
          data={candleDataPoints}
          style={{
            data: {
              stroke: getCandleColor,
              strokeWidth: 0.5,
              fill: getCandleColor,
            },
          }}
          events={[
            {
              target: 'data',
              eventHandlers: {
                onMouseOver: handleHover,
              },
            },
          ]}
        />

        {cbrtLines.map((line, index) => (
          <VictoryLine
            key={`cbrt_line_${index}`}
            data={line}
            padding={10}
            style={{
              data: {
                stroke: line[0].color,
                strokeWidth: 0.5,
              },
            }}
            labelComponent={
              <VictoryLabel
                textAnchor={'start'}
                dy={-5}
                style={{
                  fill: line[0].color,
                  fontSize: 12,
                  fontWeight: 600,
                }}
                backgroundStyle={{ fill: 'rgba(0, 0, 0, 0.5)' }}
                backgroundPadding={{ top: 4, bottom: 0, left: 2, right: 6 }}
              />
            }
          />
        ))}

        {mrcStatesLines.map((line, index) => (
          <VictoryLine
            key={`mrc_line_${index}`}
            data={line}
            style={{
              data: {
                stroke: line[0].color,
                strokeWidth: 0.5,
              },
            }}
            labelComponent={
              <VictoryLabel
                textAnchor={'start'}
                dy={line[0].orderType === 'SELL' ? 9 : -3}
                dx={1}
                style={{
                  fill: line[0].color,
                  fontSize: 12,
                  fontWeight: 600,
                }}
                backgroundStyle={{ fill: 'rgba(0, 0, 0, 0.5)' }}
                backgroundPadding={{ top: 2, left: 2, right: 2 }}
              />
            }
          />
        ))}

        <VictoryScatter
          data={transactionDataPoints}
          style={{
            data: {
              fill: ({ datum }) => datum.color,
              stroke: (d) => d.datum.strokeColor,
              strokeWidth: 2,
            },
          }}
          dataComponent={
            <Point className={'single-profit-calcs-chart-scatter-dot'} />
          }
          labelComponent={
            <VictoryTooltip
              constrainToVisibleArea
              cornerRadius={3}
              orientation={'left'}
              centerOffset={{ x: -10, y: -40 }}
              pointerOrientation="right"
              pointerWidth={20}
              flyoutStyle={{
                stroke: (d) => d.datum.color,
                strokeWidth: 1.5,
                fill: 'rgba(255, 255, 255, 0.8)',
              }}
              flyoutPadding={{ left: 20, right: 20, top: 5, bottom: 5 }}
              labelComponent={
                <VictoryLabel
                  textAnchor="start"
                  dx={(d) => (d.datum.isBuy === null ? -60 : -55)}
                  style={[
                    {
                      fill: 'rgb(0, 0, 0)',
                      fontFamily: 'monospace',
                      fontSize: 10,
                      fontWeight: 800,
                    },
                  ]}
                />
              }
            />
          }
          size={({ datum }) => datum.size}
        />
      </VictoryChart>
    </div>
  );
};

const CandleView = (props: Props) => {
  const {
    isCompact,
    transaction,
    compactTransactions,
    handleCloseChartView,
    timeFrames,
    clusteredTransactions,
    indicatorConfig,
    rsiIndicatorValuesOppositeOrder,
    rsiIndicatorValuesSameOrder,
  } = props;

  const [rootDimensions, setRootDimensions] = useState<
    ComponentDimensions | undefined
  >(undefined);
  const rootRef = useRef<any>(null);

  const [density, setDensity] = useState(1);
  const [isCompare, setIsCompare] = useState(false);

  const [candleInfo, setCandleInfo] = useState<CandleInfo | undefined>(
    transaction.candles?.higher
      ? transaction.candles.higher[transaction.candles.higher.length - 1]
      : undefined,
  );

  const [cbrtRangeStates, setCbrtRangeStates] = useState<
    CbrtRangeState[] | undefined
  >(props.cbrtRangeStates);

  const [mrcStates, setMrcStates] = useState<MrcState[] | undefined>(
    props.mrcStates,
  );

  const [profitCalcsWakeUpTimes, setProfitCalcsWakeUpTimes] = useState(
    props.wakeupTimes,
  );

  const timeFrame = useMemo(
    () => transaction.timeFrames?.higher || timeFrames?.higher,
    [timeFrames?.higher, transaction.timeFrames?.higher],
  );

  const transactionCandles = useMemo(
    () => transaction.candles?.higher,
    [transaction.candles?.higher],
  );

  const higherTCandles = useMemo(
    () => transaction.candles?.higher,
    [transaction.candles?.higher],
  );

  useEffect(() => {
    if (!!rootDimensions) {
      return;
    }

    if (!rootRef.current) {
      return;
    }

    setRootDimensions({
      height: rootRef.current.clientHeight * 0.7,
      width: rootRef.current.clientWidth,
    });
  }, [rootDimensions]);

  return (
    <div
      className="transaction-item-candle-view-root"
      style={{ flex: 1, paddingLeft: 32 }}
    >
      <div
        className="transaction-item-candle-view-back"
        onClick={handleCloseChartView}
      >
        <BiExitFullscreen color={colors.gray} size={24} />
      </div>

      <div className="transaction-item-candle-view-root" ref={rootRef}>
        {!!rootDimensions && (
          <MultiChart dimensions={rootDimensions}>
            <Chart
              density={density}
              mrcStates={mrcStates}
              isCompact={isCompact}
              timeFrame={timeFrame as TimeFrame}
              isCompare={isCompare}
              transaction={transaction}
              higherTCandles={higherTCandles}
              cbrtRangeStates={cbrtRangeStates}
              transactionCandles={transactionCandles}
              compactTransactions={compactTransactions}
              clusteredTransactions={clusteredTransactions}
              profitCalcsWakeUpTimes={profitCalcsWakeUpTimes}
              setCandleInfo={setCandleInfo}
            />
            {rsiIndicatorValuesOppositeOrder && !!indicatorConfig && (
              <RSIChart
                key={'Opposite Order Relative Strength Index'}
                label={`Opposite Order Relative Strength Index (p:${indicatorConfig.oppositeOrderPeriod})`}
                rsi={rsiIndicatorValuesOppositeOrder}
                overExtensionThreshold={indicatorConfig.oppositeOrderThreshold}
                showXAxis={false}
              />
            )}
            {rsiIndicatorValuesSameOrder && !!indicatorConfig && (
              <RSIChart
                key={'Same Order Relative Strength Index'}
                label={`Same Order Relative Strength Index (p:${indicatorConfig.sameOrderPeriod})`}
                rsi={rsiIndicatorValuesSameOrder}
                overExtensionThreshold={indicatorConfig.sameOrderThreshold}
                showXAxis={false}
              />
            )}
          </MultiChart>
        )}
      </div>
    </div>
  );
};

export default CandleView;
